import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { MoneyInput } from '../components/MoneyInput';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// Do Min (Mines, vgmn_230, task ruby-do-min): che do mo, he so (loi the nha cai) va tien thang toi da moi van, thoi gian cho.
// He so va tien thang toi da chi chu so huu doi (may chu kiem lai); han muc cuoc o trang Han muc cuoc, quy o trang Quy dieu khien.

type CheDo = 'tat' | 'test' | 'tat_ca';
type CauHinh = { cheDo: CheDo; loiNhaCai: number | null; thangToiDa: number | null; phutCho: number };
type Data = {
  cauHinh: CauHinh | null;
  phien_ban?: number;
  nguon?: 'cms' | 'mac_dinh';
  hanMuc?: { cuocMin: number | null; cuocMax: number | null; traMax: number | null; cms: { bat: boolean | null; cuocMin: number | null; cuocMax: number | null } } | null;
  quy?: { soDu: number | null; bat: boolean; vung: string | null; traToiDa: number | null; ganh: number | null } | null;
  chuSoHuu?: boolean;
  dangGui?: boolean;
  loi?: string;
};
type Form = { phien: number; cheDo: CheDo; loi: string; thang: string; phut: string };

const CHE_DO: { v: CheDo; ten: string }[] = [
  { v: 'tat', ten: 'Tắt (không nhận ván mới)' },
  { v: 'test', ten: 'Chỉ tài khoản test' },
  { v: 'tat_ca', ten: 'Mọi người chơi' },
];
const chuSo = (s: string, n: number) => s.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, n);
// he so tra khi da mo k o voi m min tren 25 o, cung cong thuc may game (backend/lib/do_min.js heSo100): C(25,k)/C(25-m,k) x (100 - loi)%
// lam tron xuong 2 so le; chi de xem truoc, tien that may game tinh
function heSo(m: number, k: number, loiBp: number) {
  let tu = 1n, mau = 1n;
  for (let i = 0; i < k; i++) { tu *= BigInt(25 - i); mau *= BigInt(25 - m - i); }
  return Number(tu * BigInt(10000 - loiBp) / (mau * 100n)) / 100;
}

function tuCauHinh(c: CauHinh, phien: number): Form {
  return { phien, cheDo: c.cheDo, loi: c.loiNhaCai == null ? '' : String(c.loiNhaCai), thang: c.thangToiDa == null ? '' : String(c.thangToiDa), phut: String(c.phutCho) };
}
function guiCua(f: Form): CauHinh {
  return { cheDo: f.cheDo, loiNhaCai: f.loi === '' ? null : Number(f.loi), thangToiDa: f.thang === '' ? null : Number(f.thang), phutCho: Number(f.phut) };
}
function loiForm(f: Form): string | null {
  if (f.loi !== '') {
    const x = Number(f.loi);
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(f.loi) || x > 50) return 'Lợi thế nhà cái từ 0 đến 50%, tối đa 2 chữ số thập phân';
  }
  if (f.thang !== '' && !(Number(f.thang) >= 1)) return 'Tiền thắng tối đa từ 1';
  const p = Number(f.phut);
  if (f.phut === '' || p < 1 || p > 1440) return 'Thời gian chờ từ 1 đến 1440 phút';
  return null;
}
const vnd = (n: number | null | undefined) => (n == null ? 'không đặt' : formatVnd(n));

export function DoMinPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['do-min'], queryFn: () => api.get<Data>('/api/admin/games/do-min'), refetchInterval: 15000 });
  const [form, setForm] = useState<Form | null>(null);
  const [xong, setXong] = useState<Command | null>(null);
  const luu = useMutation({
    mutationFn: (b: { phien_ban: number; cauHinh: CauHinh }) => api.put<{ command: Command }>('/api/admin/games/do-min', b),
    onSuccess: (r) => { setXong(r.command); setForm(null); qc.invalidateQueries({ queryKey: ['do-min'] }); },
  });

  const header = <PageHeader title="Dò Mìn" lead="Chế độ mở, hệ số, tiền thắng tối đa mỗi ván và hạn mức của Dò Mìn." />;
  if (q.isLoading) return <div className="page">{header}<BlockSkeleton lines={8} /></div>;
  if (q.error || !q.data) return <div className="page">{header}<ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const d = q.data;
  const ch = d.cauHinh;
  if (!ch) {
    return (
      <div className="page">
        {header}
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Hệ A chưa trả lời: chưa đọc được cài đặt Dò Mìn." />
      </div>
    );
  }
  const goc = tuCauHinh(ch, d.phien_ban ?? 0);
  const f = form ?? goc;
  const doi = JSON.stringify(guiCua(f)) !== JSON.stringify(guiCua(goc)) || f.phien !== goc.phien;
  const loi = loiForm(f);
  const chu = !!d.chuSoHuu;
  const sua = (x: Partial<Form>) => setForm({ ...f, ...x });
  const loiBp = f.loi === '' || loiForm({ ...f, thang: '', phut: '1' }) ? null : Math.round(Number(f.loi) * 100);
  const hm = d.hanMuc;
  const quy = d.quy;

  return (
    <div className="page">
      {header}
      {xong ? (
        <div>
          <InlineNotification kind={xong.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => { setXong(null); return false; }}
            title={xong.status === 'SUCCEEDED' ? 'Đã lưu cài đặt Dò Mìn' : 'Lệnh lưu chưa xong'}
            subtitle={xong.status === 'SUCCEEDED' ? 'Ván mới dùng cài đặt mới; ván đang chơi giữ hệ số lúc bắt đầu.'
              : (HE_A_ERRORS[xong.lastError ?? ''] ?? (xong.lastError || xong.status))} />
          <p><Link className="cell-link" to={'/commands/' + xong.id}>Xem lệnh</Link></p>
        </div>
      ) : null}
      {ch.loiNhaCai == null ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Chưa đặt hệ số"
          subtitle="Máy game từ chối mọi ván mới (kể cả tài khoản test) cho tới khi chủ sở hữu đặt lợi thế nhà cái." />
      ) : null}
      <section className="card section">
        <h3 style={{ marginTop: 0 }}>Hạn mức và quỹ</h3>
        <p style={{ margin: '0 0 .5rem' }}>
          Cược mỗi ván: <strong>{vnd(hm?.cms.cuocMin ?? hm?.cuocMin)}</strong> đến <strong>{vnd(hm?.cms.cuocMax ?? hm?.cuocMax)}</strong>
          {hm?.cms.bat === false ? <Tag type="red" size="sm">Tạm dừng ở Hạn mức cược</Tag> : null}
          <span className="muted"> · Trả tối đa theo hạn mức: {vnd(hm?.traMax)}. Sửa ở <Link className="cell-link" to="/games/limits">Hạn mức cược</Link>.</span>
        </p>
        <p style={{ margin: 0 }}>
          Quỹ Dò Mìn: <strong>{quy ? vnd(quy.soDu) : 'chưa đọc được'}</strong>
          {quy && !quy.bat ? <Tag type="gray" size="sm">Quỹ tắt</Tag> : null}
          {quy?.vung === 'do' ? <Tag type="red" size="sm">Dưới ngưỡng, nhà cái thắng</Tag> : null}
          <span className="muted"> · Quỹ lái giống các game khác: không từ chối ván và không cắt hệ số; khi mở ô vượt quỹ khả dụng / trần trả hoặc ở vùng đỏ thì ô đó bị lái trúng mìn. Sửa quỹ ở <Link className="cell-link" to="/games/mkt-quy">MKT và quỹ</Link> / <Link className="cell-link" to="/games/control">Quỹ điều khiển</Link>.</span>
        </p>
      </section>
      <form aria-label="Cài đặt Dò Mìn" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem', maxWidth: '40rem' }}
        onSubmit={(e) => { e.preventDefault(); if (doi && !loi) luu.mutate({ phien_ban: f.phien, cauHinh: guiCua(f) }); }}>
        <Select id="do-min-che-do" labelText="Chế độ mở" value={f.cheDo} onChange={(e) => sua({ cheDo: e.target.value as CheDo })}
          helperText={chu ? 'Tắt: ván đang chơi vẫn mở ô và rút được.' : 'Mở cho mọi người chơi chỉ chủ sở hữu đổi.'}>
          {CHE_DO.map((c) => <SelectItem key={c.v} value={c.v} text={c.ten} disabled={!chu && c.v === 'tat_ca' && ch.cheDo !== 'tat_ca'} />)}
        </Select>
        <TextInput id="do-min-loi" labelText="Lợi thế nhà cái (%) — hệ số" inputMode="decimal" value={f.loi} disabled={!chu}
          placeholder="chưa đặt" onChange={(e) => sua({ loi: e.target.value.replace(/[^\d.]/g, '').slice(0, 5) })}
          helperText={chu ? 'Hệ số trả = nghịch đảo xác suất sống sót × (100 − lợi thế)%. Để trống = chưa đặt: không nhận ván mới.' : 'Chỉ chủ sở hữu đổi.'} />
        <MoneyInput id="do-min-thang" labelText="Tiền thắng tối đa mỗi ván" value={f.thang} maxDigits={14} disabled={!chu}
          placeholder="không đặt" onChange={(v) => sua({ thang: v })}
          helperText={chu ? 'Để trống = chỉ theo hạn mức trả và quỹ.' : 'Chỉ chủ sở hữu đổi.'} />
        <TextInput id="do-min-phut" labelText="Ván bỏ dở tự chốt sau (phút)" inputMode="numeric" value={f.phut}
          onChange={(e) => sua({ phut: chuSo(e.target.value, 4) })} helperText="Đã mở ô: máy rút giúp; chưa mở ô: hoàn cược." />
        {loiBp != null ? (
          <div>
            <p className="muted" style={{ margin: '0 0 .25rem' }}>Xem trước hệ số (mở 1, 3, 5, 10 ô)</p>
            <table className="plain">
              <thead><tr><th>Số mìn</th><th>1 ô</th><th>3 ô</th><th>5 ô</th><th>10 ô</th></tr></thead>
              <tbody>
                {[1, 3, 5, 9, 15].map((m) => (
                  <tr key={m}>
                    <td data-label="Số mìn">{m}</td>
                    {[1, 3, 5, 10].map((k) => <td key={k} data-label={k + ' ô'}>{k <= 25 - m ? heSo(m, k, loiBp).toFixed(2) + 'x' : '—'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="row">
          <Button type="submit" size="md" disabled={!doi || !!loi || luu.isPending || !!d.dangGui}>{luu.isPending ? 'Đang lưu' : 'Lưu'}</Button>
          {form ? <Button kind="ghost" size="md" onClick={() => setForm(null)}>Hủy sửa</Button> : null}
        </div>
        {doi && loi ? <p className="muted" role="alert">{loi}</p> : null}
        {d.dangGui ? <p className="muted">Đang có một thay đổi Dò Mìn chưa gửi xong.</p> : null}
        <InlineError error={luu.error} />
      </form>
    </div>
  );
}
